import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Column } from 'src/components/table';
import { useModal } from 'src/hooks';
import { useDeleteScheduleItemOverrides } from 'src/hooks/api/schedules/useDeleteScheduleItemOverrides';
import { PublicationTypes } from 'src/interfaces';
import { lang } from 'src/utils';

import { useOOLocationSchedulePage } from './hooks/ScheduleLocationPageOO.hook';
import { OOLocationSchedulePageTable } from './modules/OOLocationSchedulePageTable/OOLocationSchedulePageTable';

const columnsDefaultAssembly = [
  {
    id: 'startDate',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
  },
  { id: 'endDate', sortKey: 'valid_to', label: lang.endDate, sortIcon: true },
  {
    id: 'assemblyName',
    label: lang.assemblyName,
    sortIcon: false,
  },
  {
    id: 'status',
    label: lang.status,
    sortIcon: false,
  },
  {
    id: 'assemblyScheduleDate',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
  },
];

const columnsScheduleComponent = [
  {
    id: 'name',
    sortKey: 'name',
    label: lang.scheduleComponent.scheduleNameField,
    sortIcon: true,
    width: 'calc(100% - 400px)',
  },
  {
    id: 'modified_at',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
    width: '240px',
    align: 'left',
  },
  {
    id: 'override',
    label: 'Override',
    sortIcon: false,
    width: '200px',
    align: 'left',
  },
  {
    id: 'latest_override_date',
    sortKey: 'latest_override_date',
    label: 'Override date',
    sortIcon: true,
    width: '200px',
    align: 'left',
  },
];

const columnsScheduleComponentExpanded: Column[] = [
  {
    id: 'start_date',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'end_date',
    sortKey: 'end_date',
    label: lang.endDate,
    key: 'endDate',
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'name',
    sortKey: 'name',
    label: 'Component name',
    sortIcon: true,
    width: 'calc(100% - 240px)',
  },
  {
    id: 'override',
    label: 'Override',
    sortIcon: false,
    width: '200px',
    align: 'left',
  },
  {
    id: 'latest_override_date',
    sortKey: 'latest_override_date',
    label: 'Override date',
    sortIcon: true,
    width: '200px',
    align: 'left',
  },
];

export const ScheduleLocationPageOO = () => {
  const {
    individualLocationScheduleData,
    formattedSchedulesData,
    formMethods,
    isLoading,
    isLoadingSchedules,
    onChangeLocationGroup,
    page,
    totalPages,
    totalSchedulesPages,
    onPageChange,
    selectedDaypartId,
    selectedPositionId,
    daypartOptions,
    isLoadingDayparts,
    onDaypartChange,
    onPositionChange,
    selectedOption,
    onSelectedOptionChange,
    projectConfig,
    isDefaultAssembly,
    locationGroup,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
    setPage,
    sortSelected,
    setSortSelected,
    totalCountDefault,
    totalCountSchedule,
    rowsPerPage,
    handleRowsPerPageChange,
    shouldShowListview,
  } = useOOLocationSchedulePage();
  const deleteOverrideModal = useModal();

  const deleteScheduleItemOverrides = useDeleteScheduleItemOverrides();
  const { locationCode = '' } = useParams();
  const publishModal = useModal();

  const handleDeleteScheduleItemOverrides = (scheduleItemId: number) => {
    deleteOverrideModal.onOpenModal(async () => {
      await deleteScheduleItemOverrides.mutateAsync({
        scheduleItemId,
        currentPosition: Number(selectedPositionId),
        currentSidebarDetailsDataParams: {
          locationCode,
          position: String(selectedPositionId),
        },
      });

      deleteOverrideModal.onCloseModal();
      publishModal.onOpenModal();
    });
  };

  const handleSortingClick = (columnId: string, direction: string) => {
    const selectedColumns = isDefaultAssembly
      ? columnsDefaultAssembly
      : columnsScheduleComponent;
    const column = selectedColumns.find((col) => col.id === columnId);

    if (!column?.sortKey) return;

    setSortKey(column.sortKey);
    setSortDirection(direction);
    setPage(1);
    setSortSelected(true);
  };

  return (
    <FormProvider {...formMethods}>
      <OOLocationSchedulePageTable
        columns={
          isDefaultAssembly ? columnsDefaultAssembly : columnsScheduleComponent
        }
        expandedColumns={
          isDefaultAssembly ? undefined : columnsScheduleComponentExpanded
        }
        tableData={
          isDefaultAssembly
            ? individualLocationScheduleData
            : formattedSchedulesData
        }
        isLoading={isDefaultAssembly ? isLoading : isLoadingSchedules}
        totalCount={isDefaultAssembly ? totalCountDefault : totalCountSchedule}
        onChangeLocationGroup={onChangeLocationGroup}
        onScheduleOverrideDelete={handleDeleteScheduleItemOverrides}
        page={page}
        totalPage={isDefaultAssembly ? totalPages : totalSchedulesPages}
        onPageChange={onPageChange}
        selectedDaypartId={selectedDaypartId}
        selectedPositionId={selectedPositionId}
        daypartOptions={daypartOptions}
        isLoadingDayparts={isLoadingDayparts}
        onDaypartChange={onDaypartChange}
        onPositionChange={onPositionChange}
        selectedOption={selectedOption}
        onSelectedOptionChange={onSelectedOptionChange}
        projectConfig={projectConfig}
        locationGroup={locationGroup}
        sortDirection={{
          key: sortKey,
          direction: sortDirection,
          isSortModified: sortSelected,
        }}
        handleSortingClick={handleSortingClick}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        shouldShowListview={shouldShowListview}
      />
      <SimpleDialog
        data-testid="delete-modal"
        open={deleteOverrideModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.deleteScheduleItemOverridesModal.title}
        subTitle={lang.deleteScheduleItemOverridesModal.subTitle}
        onClose={deleteOverrideModal.onCloseModal}
        onClickCancel={deleteOverrideModal.onCloseModal}
        modalSize="sm"
        isLoading={deleteScheduleItemOverrides.isLoading}
        onClickConfirm={deleteOverrideModal.customFn}
      />
      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          publicationType={PublicationTypes.OVERRIDES}
          locationCodes={[locationCode]}
          onClose={publishModal.onCloseModal}
        />
      )}
    </FormProvider>
  );
};
